import React, { useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useDispatch, useSelector } from "react-redux";
import { emptyTrainerByEmail, updateTrainingCenter } from "../../../Actions";
import FileInput from "./FileUpload";
import { CustomGreenButton } from "../../Button/CustomButton";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Part2 = ({
  handleBack,
  handleNext,
  parsedListOfSkills,
  trainingCenterDetails,
  setFormSuccess,
}) => {
  const dispatch = useDispatch();
  const trainersReducer = (state) => state.trainersReducer;
  const { trainingCenterUpdated, loadingUpdated } =
    useSelector(trainersReducer);

  const [selectedSkills, setSelectedSkills] = useState([]);

  const [skillsError, setSkillsError] = useState(false);
  const [skillsErrorMessage, setSkillsErrorMessage] = useState("");

  const [fileInputs, setFileInputs] = useState([]);

  const [cacCertificateFileError, setCacCertificateFileError] = useState(false);
  const [interiorPhotographFileError, setInteriorPhotographFileError] =
    useState(false);
  const [ownerPassportFileError, setOwnerPassportFileError] = useState(false);

  const cacCertificateFileRef = useRef();
  const interiorPhotographFileRef = useRef();
  const ownerPassportFileRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (trainingCenterDetails && trainingCenterDetails.skills) {
      const initialSelectedSkills = trainingCenterDetails.skills.map(
        (skill) => ({
          id: skill.id,
          label: skill.name,
        })
      );
      setSelectedSkills(initialSelectedSkills);
    }
    if (trainingCenterDetails && trainingCenterDetails.centerFiles) {
      const initialFileInputs = trainingCenterDetails.centerFiles.map(
        (file) => ({
          id: file.id,
          fileName: file.name,
          file: file.file,
          fileExtension: file.contentType,
          isOwnerPassport: file.isOwnerPassport,
        })
      );
      setFileInputs(initialFileInputs);
    }
  }, [trainingCenterDetails]);

  const handleSkillsChange = (event, newValue) => {
    setSelectedSkills(newValue);
    if (skillsError) {
      setSkillsError(false);
      setSkillsErrorMessage("");
    }
  };

  const isOptionSelected = (option) =>
    selectedSkills.some((skill) => skill.id === option.id);

  const handleFileChange = (index, newFile) => {
    const newFileInputs = [...fileInputs];
    const existingFileIndex = newFileInputs.findIndex(
      (file) => file.fileName === newFile.fileName
    );

    if (existingFileIndex !== -1) {
      newFileInputs[existingFileIndex] = newFile;
    } else {
      newFileInputs.push(newFile);
    }

    switch (index) {
      case 0:
        setCacCertificateFileError(false);
        break;
      case 1:
        setInteriorPhotographFileError(false);
        break;
      case 2:
        setOwnerPassportFileError(false);
        break;
      default:
        break;
    }

    setFileInputs(newFileInputs);
  };

  const handleSubmit = () => {
    const skillIds = selectedSkills.map((skill) => skill.id);
    
    const cacCertificateFileError = cacCertificateFileRef.current.checkFileError();
    const interiorPhotographFileError = interiorPhotographFileRef.current.checkFileError();
    const ownerPassportFileError = ownerPassportFileRef.current.checkFileError();

    setCacCertificateFileError(cacCertificateFileError);
    setInteriorPhotographFileError(interiorPhotographFileError);
    setOwnerPassportFileError(ownerPassportFileError);

    const hasFileError = cacCertificateFileError || interiorPhotographFileError || ownerPassportFileError;

    if (skillIds.length === 0 || hasFileError) {
      if (skillIds.length === 0) {
        setSkillsError(true);
        setSkillsErrorMessage("Please select at least one skill");
      }
      return;
    }

    if (skillIds.length !== 0 && !hasFileError) {
      const updatedFiles = fileInputs.filter((newFile) => {
        const existingFile = trainingCenterDetails.centerFiles.find(
          (file) => file.name === newFile.fileName
        );
        return !existingFile || newFile.file !== existingFile.file;
      });

      const payload = {
        skillCenterId: trainingCenterDetails.id,
        skillIds: skillIds,
        files: updatedFiles,
      };

      dispatch(updateTrainingCenter(payload));

      setSkillsError(false);
      setSkillsErrorMessage("");
      setCacCertificateFileError(false);
      setInteriorPhotographFileError(false);
      setOwnerPassportFileError(false);
    }
  };

  useEffect(() => {
    if (trainingCenterUpdated === true) {
      setFormSuccess(true);
      handleNext();
      dispatch(emptyTrainerByEmail());
    }
  }, [trainingCenterUpdated]);

  return (
    <>
      <Box mt={3}>
        <p style={{ marginBottom: "1rem" }}>
          1. List the skills taught in your centre*
        </p>
        <Autocomplete
          multiple
          id="checkboxes-tags-demo"
          options={parsedListOfSkills}
          disableCloseOnSelect
          getOptionLabel={(option) => option.label}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={isOptionSelected(option)}
              />
              {option.label}
            </li>
          )}
          value={selectedSkills}
          onChange={handleSkillsChange}
          style={{ width: "100%" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Skills"
              error={skillsError}
              helperText={skillsErrorMessage}
            />
          )}
        />

        <Box mt={3}>
          <FileInput
            ref={cacCertificateFileRef}
            number="2"
            fileEntry="CAC Certificate"
            fileType="CAC Business Registration Certificate*"
            existingFile={fileInputs.find(
              (file) => file.fileName === "CAC Certificate"
            )}
            onChange={(newFile) => handleFileChange(0, newFile)}
            isOwnerPassportFile={false}
          />
          {cacCertificateFileError && (
            <Typography variant="body2" color="error">
              Please upload CAC Certificate
            </Typography>
          )}
        </Box>
        <Box mt={3}>
          <FileInput
            ref={interiorPhotographFileRef}
            number="3"
            fileEntry="Interior Photograph"
            fileType="Interior Photograph of Training Center*"
            existingFile={fileInputs.find(
              (file) => file.fileName === "Interior Photograph"
            )}
            onChange={(newFile) => handleFileChange(1, newFile)}
            isOwnerPassportFile={false}
          />
          {interiorPhotographFileError && (
            <Typography variant="body2" color="error">
              Please upload Interior Photograph
            </Typography>
          )}
        </Box>
        <Box mt={3}>
          <FileInput
            ref={ownerPassportFileRef}
            number="4"
            fileEntry="Owner Passport"
            fileType="Business Owner Passport Photograph*"
            existingFile={fileInputs.find(
              (file) => file.fileName === "Owner Passport"
            )}
            onChange={(newFile) => handleFileChange(2, newFile)}
            isOwnerPassportFile={true}
          />
          {ownerPassportFileError && (
            <Typography variant="body2" color="error">
              Please upload Owner Passport
            </Typography>
          )}
        </Box>
      </Box>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "24px",
        }}
      >
        <Box style={{ marginTop: "24px" }}>
          <Button
            sx={{ textTransform: "inherit" }}
            variant="outlined"
            color="primary"
            onClick={handleBack}
          >
            Back
          </Button>
          <CustomGreenButton
            loading={loadingUpdated}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            style={{ marginLeft: "16px" }}
          >
            Submit
          </CustomGreenButton>
        </Box>
      </div>
    </>
  );
};

export default Part2;
